import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { VisitorPass } from '@condo/contracts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useVisitorPasses } from './api';
import { DataTable } from '@/components/common/data-table';
import { Badge } from '@/components/ui/badge';

function statusBadge(status: VisitorPass['status']) {
  switch (status) {
    case 'pending':
      return <Badge variant="warning">Pendiente</Badge>;
    case 'used':
      return <Badge variant="success">Ingresado</Badge>;
    case 'expired':
      return <Badge variant="neutral">Expirado</Badge>;
  }
}

function formatDt(iso: string) {
  return format(new Date(iso), 'd MMM, HH:mm', { locale: es });
}

const columns: ColumnDef<VisitorPass, unknown>[] = [
  {
    accessorKey: 'visitorNombre',
    header: 'Visitante',
    cell: ({ row }) => (
      <span className="font-medium">{row.original.visitorNombre}</span>
    ),
  },
  { accessorKey: 'visitorDni', header: 'DNI' },
  {
    accessorKey: 'unidadNumero',
    header: 'Unidad',
    cell: ({ row }) => <span>{row.original.unidadNumero}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) => statusBadge(row.original.status),
  },
  {
    accessorKey: 'createdAt',
    header: 'Creado',
    cell: ({ row }) => (
      <span className="text-[var(--color-ink-muted)]">
        {formatDt(row.original.createdAt)}
      </span>
    ),
  },
  {
    accessorKey: 'expiresAt',
    header: 'Expira',
    cell: ({ row }) => (
      <span className="text-[var(--color-ink-muted)]">
        {formatDt(row.original.expiresAt)}
      </span>
    ),
  },
];

export function VisitorPassesTab({ condoId }: { condoId: string }) {
  const { data, isLoading } = useVisitorPasses(condoId);
  const sorted = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data]);

  return (
    <DataTable
      columns={columns}
      data={sorted}
      isLoading={isLoading}
      empty={
        <span className="text-sm text-[var(--color-ink-muted)]">
          No hay pases de visita registrados.
        </span>
      }
    />
  );
}
